import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { PartnerTransferSection } from 'further-types/lp/partner-transfer';
import { CurrencyCode } from 'further-types/lp/vehicle';
import ActionCard from './ActionCard';
import useLoadPartnerTransferActions from '../../hooks/useLoadPartnerTransferActions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { PartnerTransfersFormValues } from '../../hooks/usePartnerTransfersForm';
import api from 'lib/trpcClient';
import Page from 'components/new/Page';
import { PARTNER_TRANSFERS_LIST } from 'adminConstants/routes';
import PartnerTransferActionBar from '../../PartnerTransferActionBar';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import AvatarWithName from 'components/new/AvatarWithName';
import { numberToCurrencyString, pluralize } from 'further-ui/utils';
import { ActionRefLabels } from 'further-ui/labels';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';

type TransferActionsProps = {
  partnerTransferSection: PartnerTransferSection;
  partnerTransferId: string;
  vehicleId: string;
  baseCurrency: CurrencyCode;
  onPrevious: () => void;
  onNext: () => void;
  stepper: React.ReactNode;
};

const TransferActions: React.FC<TransferActionsProps> = ({
  partnerTransferSection,
  partnerTransferId,
  vehicleId,
  baseCurrency,
  onPrevious,
  onNext,
  stepper,
}) => {
  const { expandedRows, toggleRowExpansion } = useExpandedRows();
  const { getValues, handleSubmit } =
    useFormContext<PartnerTransfersFormValues>();
  const transferDate = getValues('transferDate');
  const [isNavigating, setIsNavigating] = useState(false);

  const updateTransferMutation = api.partnerTransfers.update.useMutation(
    useHandleTrpcMutation()({
      onSuccess: () => {
        onNext();
      },
    }),
  );

  const handleNextClick = handleSubmit(
    (formValues: PartnerTransfersFormValues) => {
      setIsNavigating(true);
      const input = {
        partnerTransferId,
        vehicleId,
        transferorCommitmentId: formValues.trasferrorCommitmentId,
        transferDate: formValues.transferDate,
        currentSection: partnerTransferSection,
        actions: formValues.actions ?? [],
        transferees: formValues.transferees.map((transferee) => ({
          investorId: transferee.investorId,
          amountLcy: transferee.amountLcy,
          percentage: transferee.percentage,
        })),
      };
      updateTransferMutation.mutate(input, {
        onError: () => setIsNavigating(false),
      });
    },
  );

  const { actions } = useLoadPartnerTransferActions({
    partnerTransferId,
    vehicleId,
    partnerTransferSection,
  });

  const sectionLabels: Record<PartnerTransferSection, string> = {
    [PartnerTransferSection.PartnerTransfer]: 'partner transfer',
    [PartnerTransferSection.Investments]: 'investments',
    [PartnerTransferSection.Income]: 'income',
    [PartnerTransferSection.Expenses]: 'expenses',
    [PartnerTransferSection.LinkedCapitalActions]: 'linked capital actions',
    [PartnerTransferSection.GeneralCapitalActions]: 'general capital actions',
    [PartnerTransferSection.AdvanceSubscriptions]: 'advance subscriptions',
    [PartnerTransferSection.Fees]: 'fees',
    [PartnerTransferSection.Summary]: 'summary',
  };

  const rows = actions.map((action) => ({
    ...action,
    id: action.actionId,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'counterparty',
      label: 'Counterparty',
      render: (action) => <AvatarWithName name={action.counterpartyLabel} />,
    },
    {
      key: 'type',
      label: 'Type',
      render: (action) => {
        const baseLabel = action.actionRef
          ? ActionRefLabels[action.actionRef]
          : action.actionType;
        return action.linkedActions && action.linkedActions.length > 0
          ? `${baseLabel} and ${action.linkedActions.length} ${pluralize(
              'linked action',
              action.linkedActions.length,
            )}`
          : baseLabel;
      },
    },
    {
      key: 'date',
      label: 'Date',
      render: (action) => action.ledgerDate.toReadable(),
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (action) =>
        numberToCurrencyString(action.totalAmountLcy, {
          currency: baseCurrency,
        }),
    },
  ];

  return (
    <Page
      goBackAction={PARTNER_TRANSFERS_LIST}
      spacing="lg"
      heading="Partner transfer"
      withHeadingDivider
      customActions={stepper}
      actionBar={
        <PartnerTransferActionBar
          onPrevious={onPrevious}
          onNext={handleNextClick}
          isNextLoading={isNavigating}
          isNextDisabled={isNavigating}
          isPreviousDisabled={isNavigating}
        />
      }
    >
      <Table
        emptyMessage={`No ${sectionLabels[partnerTransferSection]} found for this partner transfer.`}
        isExpandableRow={() => true}
        isExpandedRow={(row) => expandedRows.has(row.id)}
        onRowExpand={(row) => toggleRowExpansion(row.id)}
        onRowClick={(row) => toggleRowExpansion(row.id)}
        getExpandedContent={(row, _width, rowIndex) => (
          <ActionCard
            key={rowIndex}
            actionIndex={rowIndex ?? 0}
            action={row}
            baseCurrency={baseCurrency}
            partnerTransferSection={partnerTransferSection}
            transferDate={transferDate}
          />
        )}
        rows={rows}
        columns={columns}
      />
    </Page>
  );
};

export default TransferActions;
